import moment from "moment";

/**
 * The standard window: how far ahead of expiry a subscription can be paid for again.
 *
 * An organisation can be given a wider window (up to 11 months) by a system admin, so callers
 * should pass the org's value - see useCanExtendSubscription in ./_queries. This constant is the
 * fallback for when that value isn't loaded yet, and the server's default.
 *
 * This mirrors the server rather than restating it: SharedPaymentLogic.ThrowIfPaidOrInProgress
 * rejects a farm when ExpirationDate is outside SubscriptionExtensionWindow.IsWithinWindow, i.e.
 * later than UtcNow.AddMonths(window). Six calendar months is anywhere from 181 to 184 days
 * depending on where you start, so the old "less than 182 days" rule offered the user an extension
 * the server would refuse in the short months. moment's add(months) clamps the end of the month the
 * same way .NET's AddMonths does, so this matches exactly.
 */
export const DEFAULT_EXTENSION_WINDOW_MONTHS = 6;

/** A farm with no subscription left to run, so paying for one starts a fresh year from today. */
export const isSubscriptionUnpaid = (farm) => !!farm && (farm.expired || !farm.expirationDate);

/** A farm whose subscription is still running but close enough to expiry to buy another year. */
export const canExtendSubscription = (farm, windowMonths = DEFAULT_EXTENSION_WINDOW_MONTHS) => {
    if (!farm || isSubscriptionUnpaid(farm)) return false;

    return moment.utc(farm.expirationDate).isSameOrBefore(moment.utc().add(windowMonths, "months"));
};

export const canPayForSubscription = (farm, windowMonths) => isSubscriptionUnpaid(farm) || canExtendSubscription(farm, windowMonths);

/**
 * When the year being paid for starts. An extension picks up where the current subscription ends
 * rather than resetting to today, so the remaining days aren't forfeited - the server does the same
 * in SharedPaymentLogic.OnPaid. moment(undefined) is now, which covers a farm with no expiry date.
 */
export const subscriptionStartDate = (farm) => moment.max(moment(), moment(farm?.expirationDate));

export const subscriptionEndDate = (farm) => moment(subscriptionStartDate(farm)).add(1, "years");
